import React, { useState, useEffect, useMemo, useRef } from 'react';

import { Modal } from 'flowbite';

import GeneralRiskTypeCreateModal from './GeneralRiskTypeCreateModal';
import GeneralRiskTypesTable from './GeneralRiskTypesTable';

import { modalOptions } from 'Helpers/modalOptions';

import Button from 'CommonComponents/Button';
import ConfirmationModal from 'CommonComponents/ConfirmationModal';
import Toast from 'CommonComponents/Toast';

const CREATE_MODAL_ID = 'create-general-risk-type-modal';
const DELETE_MODAL_ID = 'delete-general-risk-type-modal';

export default function GeneralRiskTypesTab(props) {
    const [types, setTypes] = useState([]);
    const [deletableType, setDeletableType] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const [actionCompleted, setActionCompleted] = useState(false);
    const [actionInProgress, setActionInProgress] = useState(false);
    const [actionFailed, setActionFailed] = useState(false);

    const [generalRiskTypeDeleteModalElement, setGeneralRiskTypeDeleteModalElement] = useState(null);
    const [generalRiskTypeCreateModalElement, setGeneralRiskTypeCreateModalElement] = useState(null);

    const generalRiskTypeDeleteModalRef = useRef();
    const generalRiskTypeCreateModalRef = useRef();

    const deleteUrl = useMemo(() => deletableType && props.deleteUrl.replace('TYPE_ID', deletableType.id), [deletableType]);

    useEffect(() => {
        fetchGeneralRiskTypes();
        generalRiskTypeDeleteModalRef && setGeneralRiskTypeDeleteModalElement(new Modal(generalRiskTypeDeleteModalRef.current, modalOptions(false)));
        generalRiskTypeCreateModalRef && setGeneralRiskTypeCreateModalElement(new Modal(generalRiskTypeCreateModalRef.current, modalOptions(false)));
    }, []);

    useEffect(() => {
        deletableType && generalRiskTypeDeleteModalElement && generalRiskTypeDeleteModalElement.show();
    }, [deletableType]);

    const showModal = (modalElement) => {
        modalElement && modalElement.show();
    };

    // After a create, edit or delete: reload the list and tell an embedding
    // page that the data its status depends on has changed.
    const refetchAfterChange = () => {
        fetchGeneralRiskTypes();

        if (props.onChanged) {
            props.onChanged();
        }
    };

    const fetchGeneralRiskTypes = () => {
        setIsLoading(true);
        axios
            .get(props.fetchUrl)
            .then(({ data }) => {
                setTypes([...data]);
            })
            .catch((e) => {
                console.error(e);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const cleanConfirmationModalStates = () => {
        setActionInProgress(false);
        setActionFailed(false);
        setActionCompleted(false);
    };

    const trigerDeleteType = () => {
        setActionInProgress(true);

        axios
            .delete(deleteUrl)
            .then((res) => {
                generalRiskTypeDeleteModalElement && generalRiskTypeDeleteModalElement.hide();
                refetchAfterChange();
                $('#general-risk-type-deleted-toast').toast('show');
            })
            .catch((e) => {
                console.log(e);
            })
            .finally(() => {
                setActionInProgress(false);
            });
    };

    return (
        <div>
            <div className={'mb-2 d-flex justify-content-end'}>
                <Button variant="primary" onClick={() => showModal(generalRiskTypeCreateModalElement)}>
                    {$t('Create Risk Type')}
                </Button>
            </div>

            <GeneralRiskTypesTable types={types} isLoading={isLoading} updateUrl={props.updateUrl} fetch={refetchAfterChange} onDelete={setDeletableType} />
            <GeneralRiskTypeCreateModal
                modalId={CREATE_MODAL_ID}
                modalRef={generalRiskTypeCreateModalRef}
                modalElement={generalRiskTypeCreateModalElement}
                onCreated={refetchAfterChange}
                storeUrl={props.storeUrl}
            />
            <ConfirmationModal
                successHandler={refetchAfterChange}
                triggerAction={trigerDeleteType}
                cleanStates={cleanConfirmationModalStates}
                allowAction={deletableType}
                modalId={DELETE_MODAL_ID}
                modalRef={generalRiskTypeDeleteModalRef}
                modalElement={generalRiskTypeDeleteModalElement}
                request={actionInProgress}
                actionCompleted={actionCompleted}
                actionFailed={actionFailed}
                confirmationMsg={$t('Are you sure you want to delete this {{title}}?', { title: deletableType?.title })}
                actionCompletedMsg=""
                actionFailedMsg=""
                title={$t('Delete {{title}}', { title: deletableType?.title })}
                cancelButtonText="Cancel"
                primaryButtonText="Delete"
            />

            <Toast id="general-risk-type-update-toast" message={$t('general risk type updated successfully')} />
            <Toast id="general-risk-type-update-failed-toast" message={$t('general risk type update failed')} />
            <Toast id="general-risk-type-deleted-toast" message={$t('general risk type deleted successfully')} />
        </div>
    );
}
